// One option in the swap sheet: the same team, the same week, described the
// same way as its row on the lineup itself.
//
// A swap is chosen from this list, so the list has to carry what the decision
// needs: who the team plays, home or away, the line, whether it is a Super
// Dog, whether the game has started, the score so far, and whether the team
// is locked. A sheet of bare names asked a manager to choose from memory, or
// to close it and go back to the page they were already looking at.
//
// Everything is derived from the same helpers TeamSlot uses, off the same
// gamesBySlug map WeeklyLineupManager builds once for the week, so the row
// and the sheet cannot disagree about a team.
import React from 'react';
import { Lock, Snowflake } from 'lucide-react';
import { weeklyLineupUtils } from '../../utils/weeklyLineupUtils';
import {
  getGameDisplayInfo,
  getGameSpreadDisplay,
  isSuperDog,
  isTeamLocked,
  calculateCombinedPoints,
  shouldShowSpread,
} from '../../utils/gameStatus';
import { getWeeklyPoints } from '../../utils/teamStats';
import { SUPER_DOG_MARK, SUPER_DOG_LEGEND } from '../../utils/superDog';

const SLOT_LABEL = { captain: 'CAPT', starters: 'ST', bench: 'BE' };
const SLOT_TONE = {
  captain: { color: 'var(--color-captain-deep)', border: 'var(--color-captain-border)', background: 'var(--color-captain-fill)' },
  starters: { color: 'var(--color-brand)', border: 'var(--color-brand-border)', background: 'var(--color-bg-card)' },
  bench: { color: 'var(--color-text-muted)', border: 'var(--color-border)', background: 'var(--color-bg-card)' },
};

function SlotBadge({ kind }) {
  const tone = SLOT_TONE[kind];
  return (
    <span style={{
      minWidth: 40, textAlign: 'center', padding: '3px 7px', borderRadius: 999,
      border: `1.5px solid ${tone.border}`, backgroundColor: tone.background,
      color: tone.color, fontSize: 10, fontWeight: 700, flexShrink: 0,
    }}>
      {SLOT_LABEL[kind]}
    </span>
  );
}

export default function SwapOptionRow({
  team,
  kind,              // 'captain' | 'starters' | 'bench'
  week,
  captain,
  tripPlayTeam,
  frozenTeams = [],
  frozenPoints = {},
  gamesBySlug = {},
  weeklyPointsBySlug = {},
  allTeams,
  TeamLogo,
  disabled = false,
  onSelect,
}) {
  const slug = team ? weeklyLineupUtils.normalizeTeamName(team) : null;
  const gameInfo = slug ? gamesBySlug[slug] : null;
  const gameDisplayInfo = team ? getGameDisplayInfo(gameInfo, slug, allTeams) : null;
  const lockStatus = team ? isTeamLocked(gameInfo, slug) : { locked: false };

  const spread = team && gameDisplayInfo && shouldShowSpread(gameDisplayInfo)
    ? getGameSpreadDisplay(gameInfo, slug)
    : null;
  const superDog = !!spread && isSuperDog(gameInfo, slug);

  const isCaptain = !!slug && captain === slug;
  const isTripPlay = !!slug && tripPlayTeam === slug;
  const isFrozen = !!slug && frozenTeams.includes(slug);

  // Same rule as the lineup row: a game that has not kicked off shows a dash,
  // not a zero, and a frozen team shows the score it was frozen at.
  const gameStarted = gameDisplayInfo
    && gameDisplayInfo.type !== 'upcoming'
    && gameDisplayInfo.type !== 'bye-week'
    && gameDisplayInfo.hasStarted !== false;
  const freshPoints = slug ? weeklyPointsBySlug?.[slug] : null;
  const basePoints = team
    ? (freshPoints ? getWeeklyPoints({ weeklyPoints: freshPoints }, week) : getWeeklyPoints(team.currentSeason, week))
    : 0;
  const storedFrozen = isFrozen ? frozenPoints[slug] : undefined;
  const points = typeof storedFrozen === 'number'
    ? storedFrozen
    : gameStarted
      ? calculateCombinedPoints(basePoints, isCaptain, isTripPlay).finalPoints
      : null;

  const matchup = gameDisplayInfo?.matchup || gameDisplayInfo?.subtext;
  const stateLine = !team ? 'Nothing in this slot'
    : !gameDisplayInfo ? 'No game scheduled'
    : gameDisplayInfo.type === 'bye-week' ? 'Bye week'
    : matchup;
  const statusLine = !team || !gameDisplayInfo || gameDisplayInfo.type === 'bye-week' ? null
    : gameDisplayInfo.isLive ? `LIVE · ${gameDisplayInfo.display}`
    : gameDisplayInfo.display;

  return (
    <button
      type="button"
      onClick={onSelect}
      disabled={disabled || lockStatus.locked}
      style={{
        display: 'flex', alignItems: 'center', gap: 10, width: '100%',
        padding: '10px 4px', minHeight: 60, background: 'none',
        border: 'none', borderBottom: '1px solid var(--color-bg-subtle)', textAlign: 'left',
        cursor: disabled || lockStatus.locked ? 'default' : 'pointer',
        opacity: disabled || lockStatus.locked ? 0.55 : 1,
      }}
    >
      <SlotBadge kind={kind} />

      <div style={{ position: 'relative', flexShrink: 0 }}>
        {team
          ? <TeamLogo teamName={team.school} size={28} clickable={false} />
          : <div style={{ width: 28, height: 28, borderRadius: '50%', backgroundColor: 'var(--color-bg-subtle)' }} />}
        {isFrozen && (
          <div style={{ position: 'absolute', bottom: -3, left: -3, borderRadius: 999, padding: 2, backgroundColor: 'var(--color-freeze)' }}>
            <Snowflake size={9} color="var(--color-bg-card)" />
          </div>
        )}
        {lockStatus.locked && !isFrozen && (
          <div style={{ position: 'absolute', bottom: -3, left: -3, borderRadius: 999, padding: 2, backgroundColor: 'var(--color-danger)' }}>
            <Lock size={9} color="var(--color-bg-card)" />
          </div>
        )}
      </div>

      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 5, minWidth: 0 }}>
          <span style={{
            fontSize: 15, fontWeight: 600, whiteSpace: 'nowrap', overflow: 'hidden',
            textOverflow: 'ellipsis', color: !team ? 'var(--color-text-muted)' : lockStatus.locked ? 'var(--color-danger)' : 'var(--color-text-strong)',
          }}>
            {team ? (team.school || team.name) : 'Empty slot'}
          </span>
          {spread && (
            <span
              style={{
                flexShrink: 0, borderRadius: 4, padding: '1px 5px', fontSize: 10, fontWeight: 700,
                ...(spread === 'PICK'
                  ? { backgroundColor: 'var(--color-bg-page)', color: 'var(--color-text-muted)' }
                  : spread.startsWith('-')
                    ? { backgroundColor: 'var(--color-band-bg)', color: 'var(--color-brand)' }
                    : { backgroundColor: 'var(--color-captain-fill)', color: 'var(--color-captain-text)' }),
              }}
              title={superDog ? SUPER_DOG_LEGEND : undefined}
            >
              {spread}
              {superDog && <span aria-label="Super Dog"> {SUPER_DOG_MARK}</span>}
            </span>
          )}
        </div>
        <div style={{ fontSize: 12, color: 'var(--color-text-muted)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
          {stateLine}
        </div>
        <div style={{ fontSize: 11, color: 'var(--color-text-muted)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
          {lockStatus.locked ? 'Locked, its game has started' : statusLine || ' '}
        </div>
      </div>

      <span style={{
        fontSize: 14, fontWeight: 700, flexShrink: 0,
        color: isFrozen ? 'var(--color-freeze)' : isCaptain ? 'var(--color-captain-deep)' : isTripPlay ? 'var(--color-tripplay)' : 'var(--color-brand)',
      }}>
        {points ?? '—'}
      </span>
    </button>
  );
}
